import React, { useEffect, useState, useCallback } from 'react';
import { Button, Form } from 'react-bootstrap';
import { updatePaymentReceiptSuggestedTip } from '../../../../../../../../../crud/payments_api/payment_receipts';
import './TipSection.scss';
import { toast } from 'react-toastify';
import { getLocalConfigurationByLocalId } from '../../../../../../../../../crud/management_api/local_configurations';
import { formatCurrency } from '../../../../../../../../../utils/functions';

interface TipSectionProps {
  receipt: any;
  receipts: any[];
  setReceipts: any;
  tipAmount: number;
  setTipAmount: any;
  totalCuenta: number;
  setTotalCuenta: any;
  couponAmount: number;
  discountAmount: number;
  disabled?: boolean;
}

const TipSection: React.FC<TipSectionProps> = ({
  receipt,
  receipts,
  setReceipts,
  tipAmount,
  setTipAmount,
  totalCuenta,
  couponAmount,
  discountAmount,
  disabled = false,
}) => {
  const [defaultTip, setDefaultTip] = useState<number>(0);
  const [manualTipInput, setManualTipInput] = useState<string>('');
  const [isManualInputMode, setIsManualInputMode] = useState<boolean>(false);
  const netBase = totalCuenta - couponAmount - discountAmount;

  // send to backend + update local state
  const applyTip = useCallback(async (suggested_tip: number) => {
    if (Number.isNaN(suggested_tip)) return;
    const safe_tip = Math.max(0, Math.round(suggested_tip));

    // only do it if there are changes that are greater thasn 0.1%
    if (
      Math.abs(tipAmount - safe_tip) < 0.0001 ||
      Math.abs(Math.max(0, netBase) * defaultTip - safe_tip) < 0.0001
    ) {
      return;
    }
    try {
      await updatePaymentReceiptSuggestedTip(receipt.receipt_id, safe_tip);
      setTipAmount(safe_tip);
      const updated = receipts.map(r =>
        r.receipt_id === receipt.receipt_id
          ? { ...r, suggested_tip: safe_tip }
          : r
      );
      setReceipts(updated);
      // Solo notificar si no fue reseteado a 0 por un descuento del 100%
      if (safe_tip > 0 || tipAmount > 0) {
        toast.success('Propina sugerida actualizada');
      }
    } catch {
      toast.error('Error actualizando propina sugerida');
    }
  }, [tipAmount, netBase, defaultTip, receipt.receipt_id, receipts, setReceipts, setTipAmount]);

  // 1) INITIALIZATION: once on mount or when we switch to a different receipt
  useEffect(() => {
    // Reset manual input mode when switching receipts
    setIsManualInputMode(false);
    setManualTipInput('');
    
    if (receipt.suggested_tip != null && receipt.suggested_tip >= 0) {
      setTipAmount(receipt.suggested_tip);
      setDefaultTip(netBase > 0 ? receipt.suggested_tip / netBase : 0);
    } else {
      const user = JSON.parse(localStorage.getItem('user') ?? '{}');
      const localId = Number(user.local_id) || 0;
      getLocalConfigurationByLocalId(localId)
        .then(resp => {
          const pct = resp.data?.default_tip ?? 0;
          const decimal = pct / 100;
          setDefaultTip(decimal);
          const suggested = Math.max(0, netBase) * decimal;
          applyTip(suggested);
        })
        .catch(err => console.error('Error al cargar configuración:', err));
    }
  }, [receipt.receipt_id, receipt.suggested_tip, netBase, setTipAmount, applyTip]);

  // 2) RE-CALCULATION: only when the bill total (netBase) changes
  useEffect(() => {
    if (defaultTip > 0 || netBase <= 0) {
      const newTip = Math.max(0, netBase) * defaultTip;
      applyTip(newTip);
    }
  }, [netBase, defaultTip, applyTip]);

  const handleSetTip = (percentage: number) => {
    // Exit manual input mode when percentage button is clicked
    setIsManualInputMode(false);
    setManualTipInput('');
    
    const decimal = percentage / 100;
    setDefaultTip(decimal);
    const suggested = netBase * decimal;
    applyTip(suggested);
  };

  const handleIsSuggestedTip = (perc: number) => {
    const decimal = perc / 100;
    return (
      Math.abs(defaultTip - decimal) < 0.0001 &&
      Math.abs(tipAmount - netBase * decimal) < 0.01
    );
  };

  const handleManualTipChange = (value: string) => {
    // Allow only numbers, dots, and commas for decimal input
    const cleanValue = value.replace(/[^0-9.,]/g, '');
    setManualTipInput(cleanValue);
  };

  const confirmManualTip = () => {
    if (!manualTipInput.trim()) return;
    
    // Convert comma to dot for decimal parsing and parse as number
    const cleanValue = manualTipInput.replace(',', '.');
    const numericValue = Number(cleanValue);
    
    if (!isNaN(numericValue) && numericValue >= 0) {
      applyTip(numericValue);
      setIsManualInputMode(false);
      setManualTipInput('');
    } else {
      toast.error('Por favor ingrese un valor válido');
    }
  };

  const cancelManualTip = () => {
    setIsManualInputMode(false);
    setManualTipInput('');
  };

  const handleTipInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmManualTip();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelManualTip();
    }
  };

  const startManualEdit = () => {
    setManualTipInput(tipAmount.toString());
    setIsManualInputMode(true);
  };

  return (
    <div className={`tip-section-card ${disabled ? 'disabled' : ''}`}>
      <div className="tip-section-single-row">
        <div className="tip-buttons">
          {[0, 10, 15, 20].map(pct => (
            <Button
              key={pct}
              variant="none"
              className={handleIsSuggestedTip(pct) ? 'clicked' : 'unclicked'}
              onClick={() => handleSetTip(pct)}
              disabled={disabled}
            >
              {pct}%
            </Button>
          ))}
        </div>

        <div className="tip-display-wrapper">
          {!isManualInputMode ? (
            <div 
              className={`tip-display-amount ${disabled ? 'disabled' : ''}`}
              onClick={disabled ? undefined : startManualEdit}
              role={disabled ? undefined : "button"}
              tabIndex={disabled ? -1 : 0}
              onKeyPress={(e) => !disabled && e.key === 'Enter' && startManualEdit()}
              title={disabled ? "Recibo pagado" : "Clic para editar propina"}
            >
              {formatCurrency(tipAmount)}
            </div>
          ) : (
            <div className="tip-input-container">
              <Form.Control
                className="tip-input"
                type="text"
                placeholder="Ingrese propina..."
                value={manualTipInput}
                onChange={e => handleManualTipChange(e.target.value)}
                onKeyDown={handleTipInputKeyPress}
                autoFocus
                disabled={disabled}
              />
              <Button
                variant="none"
                className="tip-confirm-button"
                onClick={confirmManualTip}
                disabled={!manualTipInput.trim() || disabled}
              >
                Confirmar
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TipSection;
